import { ChevronDown, LogOut, Search, UserRound } from "lucide-react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { NotificationBell } from "@/widgets/NotificationBell";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { SidebarTrigger } from "@/shared/ui/sidebar";
import { ShellContextStrip } from "@/widgets/shell/ShellContextStrip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonationOptional } from "@/contexts/ImpersonationContext";
import { profileService } from "@/features/profile";
import { useNavigate } from "react-router-dom";

export function TopNavbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // 1004 F1b:代客态右上角不露内部角色名(平台超管 / 超级管理员)—— 显示「代客中 · 平台支持」+ 被代客企业名,
  //   菜单只留「退出代客」(个人中心 / 退出登录在代客态都不对:前者是客户的资料,后者会把平台会话一起登出)。
  const { isImpersonating, meta: impMeta, stopImpersonation } = useImpersonationOptional();

  // 头像走 /me 缓存(与个人中心同 queryKey,改头像后 invalidate 即刷新)
  const { data: profile } = useQuery({
    queryKey: ["me/profile"],
    queryFn: () => profileService.getProfile(),
    staleTime: 60_000,
  });
  const avatarUrl = profile?.avatarUrl ?? null;

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const displayName = isImpersonating
    ? t("topNavbar.impersonatingName")
    : user?.name || t("topNavbar.fallbackName");
  const initial = isImpersonating
    ? displayName.charAt(0)
    : user?.name?.charAt(0) || t("topNavbar.fallbackName").charAt(0);
  const isTenant = user?.scope === "TENANT";
  const isPartner = user?.scope === "PARTNER";
  const openSearch = () => window.dispatchEvent(new Event("app:open-command-palette"));

  return (
    <header
      data-print-hide
      className="h-14 border-b bg-card flex items-center justify-between gap-3 px-4 shrink-0"
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* ☰ 只有平台超管用得上:sidebar 态的唯一消费者是 AppSidebar(平台树),
            租户壳是 74px 图标轨 + 本条上下文条,点它是空操作 —— 空操作的控件不该在。 */}
        {!isTenant && <SidebarTrigger />}
        {/* 中段 = 当前轨项的**下一层**(群 → 已开舱 / 支撑区 → 页面 / 首页留空)。
            替代 0817 那排「首页 + 5 群」—— 那是把左轨同一层镜像上来,重复而非层级。 */}
        <ShellContextStrip variant="inline" />
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {/* A1:全局搜索固定位 —— 点开 ⌘K 命令面板(跳群/舱)。窄屏也在,不再藏。 */}
        {isTenant && (
          <button
            type="button"
            onClick={openSearch}
            aria-label={t("shell.commandPlaceholder")}
            className="flex items-center gap-2 rounded-lg border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground hover:bg-muted"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">{t("shell.commandPlaceholder")}</span>
          </button>
        )}
        <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
          {/* OEM F2:渠道管理员没有租户名 —— 不摆孤零零的「—」占位 */}
          {isImpersonating ? (
            <>
              {impMeta?.tenantName && (
                <span className="font-medium text-foreground max-w-[8rem] truncate">
                  {impMeta.tenantName}
                </span>
              )}
              <Badge
                variant="secondary"
                className="text-2xs px-1.5 py-0"
                data-testid="topnav-impersonating-badge"
              >
                {t("topNavbar.impersonatingBadge")}
              </Badge>
            </>
          ) : (
            <>
              {!(isPartner && !user?.tenantName) && (
                <span className="font-medium text-foreground max-w-[8rem] truncate">
                  {user?.tenantName || t("topNavbar.noValue")}
                </span>
              )}
              {user && (
                <Badge variant="secondary" className="text-2xs px-1.5 py-0">
                  {/* 中文沿用后端 roleLabel(逐字不变);英文按角色键翻译,缺键回落 roleLabel */}
                  {t(`topNavbar.roles.${user.role}`, { defaultValue: user.roleLabel })}
                </Badge>
              )}
            </>
          )}
        </div>
        {isTenant && <NotificationBell />}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 text-sm">
              {avatarUrl && !isImpersonating ? (
                <img
                  src={avatarUrl}
                  alt={user?.name || ""}
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold bg-gradient-to-br from-primary to-chart-2 text-primary-foreground shadow-[0_0_0_1px_hsl(var(--primary)/0.6),0_0_12px_hsl(var(--primary)/0.6)]">
                  {initial}
                </div>
              )}
              <span className="hidden md:inline text-foreground">{displayName}</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {isImpersonating ? (
              <DropdownMenuItem className="text-destructive" onClick={() => stopImpersonation()}>
                <LogOut className="h-3.5 w-3.5 mr-2" />
                {t("impersonation.exit")}
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <UserRound className="h-3.5 w-3.5 mr-2" />
                  {t("topNavbar.profileMenu")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={handleLogout}>
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  {t("topNavbar.logout")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
